import { Autocomplete, TextField } from '@mui/material';
import * as moment from 'moment-timezone';
import React from 'react';

/** Suffix: numeric GMT±hh:mm vs abbreviated tz token from moment (`z`). */
const getTimezoneLabelSuffix = (zoneName, abbreviate) => {
    const m = moment.tz(zoneName);
    if (abbreviate) {
        const ab = m.format('z');
        return ab ? ab.trim() : `GMT${m.format('Z')}`;
    }
    return `GMT${m.format('Z')}`;
};

/**
 * Visible option label — full IANA, or abbreviated token only (`abbrevOnly`).
 * Search still matches IANA + label via filterOptions below.
 */
const makeZoneLabel = (tz, showTimezoneOffset, abbreviatedInParens, abbrevOnlyVisible) => {
    if (!showTimezoneOffset) {
        return tz;
    }
    if (abbrevOnlyVisible) {
        return getTimezoneLabelSuffix(tz, true);
    }
    const suffix = abbreviatedInParens ? getTimezoneLabelSuffix(tz, true) : getTimezoneLabelSuffix(tz, false);
    return `${tz} (${suffix})`;
};

const buildOptions = ({ showTimezoneOffset, abbreviatedTimezoneLabels, abbrevOnlyTimezoneLabels, includeUtc }) => {
    const timeZones = moment.tz.names();
    const base = [];

    for (let i = 0; i < timeZones.length; i++) {
        const tz = timeZones[i];
        if (tz === 'UTC' || tz === 'Etc/UTC') {
            continue;
        }

        base.push({
            label: makeZoneLabel(tz, showTimezoneOffset, abbreviatedTimezoneLabels || abbrevOnlyTimezoneLabels, abbrevOnlyTimezoneLabels),
            value: tz,
        });
    }

    if (includeUtc) {
        const utcMoment = moment.tz('UTC');
        let utcEntryLabel;
        if (!showTimezoneOffset) {
            utcEntryLabel = 'UTC';
        } else if (abbrevOnlyTimezoneLabels) {
            utcEntryLabel = utcMoment.format('z')?.trim() || 'UTC';
        } else if (abbreviatedTimezoneLabels) {
            utcEntryLabel = `UTC (${getTimezoneLabelSuffix('UTC', true)})`;
        } else {
            utcEntryLabel = `UTC (${getTimezoneLabelSuffix('UTC', false)})`;
        }

        base.unshift({
            label: utcEntryLabel,
            value: 'UTC',
        });
    }

    return base;
};

const TimezonePicker = ({
    showTimezoneOffset,
    abbreviatedTimezoneLabels = false,
    abbrevOnlyTimezoneLabels = false,
    variant,
    helperText,
    includeUtc = false,
    ...props
}) => {
    const [requiredError, setRequiredError] = React.useState(false);

    const options = React.useMemo(
        () =>
            buildOptions({
                showTimezoneOffset,
                abbreviatedTimezoneLabels,
                abbrevOnlyTimezoneLabels,
                includeUtc,
            }),
        [showTimezoneOffset, includeUtc, abbreviatedTimezoneLabels, abbrevOnlyTimezoneLabels]
    );

    const filterTimezoneOptions = React.useMemo(() => {
        return (items, params) => {
            const raw = params.inputValue.toLowerCase().trim();
            if (!raw) {
                return items;
            }

            const q = raw.replace(/\s+/g, ' ');
            return items.filter(({ label, value }) => {
                const v = `${value} ${label}`.toLowerCase();
                const vUnderscores = `${value.replace(/_/g, ' ')} ${label}`.toLowerCase();
                return v.includes(q) || vUnderscores.includes(q);
            });
        };
    }, []);

    const value = React.useMemo(() => {
        if (!props.value) return null;

        if (typeof props.value === 'string') {
            return options.find(opt => opt.value === props.value) || { value: props.value, label: props.value };
        }

        if (typeof props.value === 'object' && props.value.value) {
            return options.find(opt => opt.value === props.value.value) || props.value;
        }

        return null;
    }, [props.value, options]);

    return (
        <Autocomplete
            freeSolo={false}
            options={options}
            filterOptions={filterTimezoneOptions}
            isOptionEqualToValue={(option, val) => option?.value === (val?.value ?? val)}
            getOptionLabel={(option) => {
                if (typeof option === 'string') {
                    const foundOption = options.find(opt => opt.value === option);
                    return foundOption ? foundOption.label : option;
                }
                return option.label || '';
            }}
            value={value}
            renderInput={(params) => (
                <TextField
                    {...params}
                    required
                    label={props.label}
                    variant={variant}
                    helperText={helperText}
                    onBlur={() => {
                        props.value ? setRequiredError(false) : setRequiredError(true);
                    }}
                    error={requiredError && !props.value}
                />
            )}
            {...props}
        />
    );
};

export default TimezonePicker;
